import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/lib/auth';
import { Toaster } from '@/components/ui/sonner';
import RefTracker from '@/components/RefTracker';
import YandexMetrikaTracker from '@/components/YandexMetrikaTracker';
import ScrollManager from '@/components/ScrollManager';
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { trackPageView } from '@/lib/analytics';

// ==================== PRIORITY 1: Eager (main page ONLY) ====================
import NewLandingPage from '@/pages/NewLandingPage';
import CategoryPage from '@/pages/CategoryPage';

// ==================== PRIORITY 1.5: Horary (lazy, preloaded on hover/idle) ====================
const HoraryPage = lazy(() => import('@/pages/horary/HoraryPage'));

// ==================== PRIORITY 2: Auth (lazy, small) ====================
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const SocialAuthCallback = lazy(() => import('@/pages/auth/SocialAuthCallback'));
const UnlockPage = lazy(() => import('@/pages/auth/UnlockPage'));
const SavedResultPage = lazy(() => import('@/pages/account/SavedResultPage'));
const ConfirmSocialLinkPage = lazy(() => import('@/pages/auth/ConfirmSocialLinkPage'));

// ==================== PRIORITY 3: Everything else (lazy) ====================
const HomePage = lazy(() => import('@/pages/HomePage'));
const VerifyPage = lazy(() => import('@/pages/auth/VerifyPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const AppsCatalogPage = lazy(() => import('@/pages/apps/AppsCatalogPage'));
const AppDetailPage = lazy(() => import('@/pages/apps/AppDetailPage'));
const AccountPage = lazy(() => import('@/pages/account/AccountPage'));
const PaymentsHistoryPage = lazy(() => import('@/pages/account/PaymentsHistoryPage'));
const PaymentSuccessPage = lazy(() => import('@/pages/payment/PaymentSuccessPage'));
const PaymentCancelPage = lazy(() => import('@/pages/payment/PaymentCancelPage'));
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));
const ManagerPage = lazy(() => import('@/pages/manager/ManagerPage'));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage'));
const SubscribePage = lazy(() => import('@/pages/SubscribePage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'));
const PayAgainPage = lazy(() => import('@/pages/payment/PayAgainPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ConsultationPage = lazy(() => import('@/pages/ConsultationPage'));
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'));
const OfferPage = lazy(() => import('@/pages/legal/OfferPage'));
const PartnerOfferPage = lazy(() => import('@/pages/legal/PartnerOfferPage'));
const MailingConsentPage = lazy(() => import('@/pages/legal/MailingConsentPage'));
const ContactsLegalPage = lazy(() => import('@/pages/legal/ContactsPage'));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));
const PaymentHelpPage = lazy(() => import('@/pages/support/PaymentHelpPage'));
const ManualPaymentPage = lazy(() => import('@/pages/payment/ManualPaymentPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const NatalSeoLandingPage = lazy(() => import('@/pages/seo/NatalSeoLandingPage'));
const LandingTemplate = lazy(() => import('@/pages/LandingTemplate'));
const SitemapPage = lazy(() => import('@/pages/SitemapPage'));
const CmsPublicPage = lazy(() => import('@/pages/cms/CmsPublicPage'));
const SharePreviewPage = lazy(() => import('@/pages/SharePreviewPage'));
const ArticlesListPage = lazy(() => import('@/pages/cms/ArticlesListPage'));
const EmailPreferencesPage = lazy(() => import('@/pages/EmailPreferencesPage'));
const ProfilePage = lazy(() => import('@/pages/lk/ProfilePage'));
const DailyReportPage = lazy(() => import('@/pages/lk/DailyReportPage'));
const Daily3BirthDataPage = lazy(() => import('@/pages/apps/daily3/Daily3BirthDataPage'));
const Daily3ResultsPage = lazy(() => import('@/pages/apps/daily3/Daily3ResultsPage'));
const LovePeriodsPage = lazy(() => import('@/pages/apps/love/LovePeriodsPage'));
const MoneyPeriodsPage = lazy(() => import('@/pages/apps/money-periods/MoneyPeriodsPage'));
const YearPage = lazy(() => import('@/pages/apps/year/YearPage'));
// «Где тебе лучше жить» — этап 1, только владелице и админам (страница сама
// отвечает «не найдено» остальным; в меню и sitemap её нет).
const WhereToLivePage = lazy(() => import('@/pages/apps/where-to-live/WhereToLivePage'));
const AnketaPage = lazy(() => import('@/pages/anketa/AnketaPage'));
const MumV2Page = lazy(() => import('@/pages/apps/mum-v2/MumV2Page'));
// Прежняя «Нумерология» снята с маршрута 12.10.2026, файл оставлен для отката:
// const NumCalculatorPage = lazy(() => import('@/pages/apps/num/NumCalculatorPage'));
const NumV2Page = lazy(() => import('@/pages/apps/num-v2/NumV2Page'));
const WorkV2Page = lazy(() => import('@/pages/apps/work-v2/WorkV2Page'));
const UzelV2Page = lazy(() => import('@/pages/apps/uzel-v2/UzelV2Page'));
const ParaCalculatorPage = lazy(() => import('@/pages/apps/para/ParaCalculatorPage'));
const ChakryPage = lazy(() => import('@/pages/apps/chakry/ChakryPage'));
const ObrazV2Page = lazy(() => import('@/pages/apps/obraz-v2/ObrazV2Page'));
const VlyubitPage = lazy(() => import('@/pages/apps/vlyubit/VlyubitPage'));
const MoneyV2Page = lazy(() => import('@/pages/apps/money-v2/MoneyV2Page'));
const MonthV2Page = lazy(() => import('@/pages/apps/month-v2/MonthV2Page'));
const WomenV2Page = lazy(() => import('@/pages/apps/women-v2/WomenV2Page'));
const LunarCalendarPage = lazy(() => import('@/pages/lunar/LunarCalendarPage'));
const VotePage = lazy(() => import('@/pages/VotePage'));
const AccessGuard = lazy(() => import('@/components/AccessGuard'));

// Global SEO meta for private/technical routes (auth, lk, payment, admin, etc.)
import NoIndexMeta from '@/components/NoIndexMeta';
// Canonical tag pointing to the cyrillic primary domain (астрофил.рф).
// Mounted at the route level so canonicals are emitted even for pages
// gated by AccessGuard (where the inner page component never mounts for
// unauthenticated crawlers).
import CanonicalRf from '@/components/CanonicalRf';

// Путь статей CMS — переменная сборки REACT_APP_ARTICLES_PATH (по умолчанию
// `/stranitsy`, то есть астрофил.рф не меняется). См. src/lib/siteConfig.js.
import { ARTICLES_PATH, ARTICLES_PATH_IS_DEFAULT, LEGACY_ARTICLES_PATH } from '@/lib/siteConfig';
import { useRubProvider } from '@/lib/rubProvider';

// ScenarioBot — lazy, loaded after main content
const ScenarioBot = lazy(() => import('@/components/ScenarioBot'));

// Minimal spinner for lazy loading
const PageSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner"></div>
  </div>
);

// Suspense wrapper — only for lazy-loaded pages
const Lazy = ({ children }) => (
  <Suspense fallback={<PageSpinner />}>{children}</Suspense>
);

// Lazy wrapper for private / technical pages — also injects <meta name="robots" content="noindex, follow">
// so these URLs don't end up in Google/Яндекс search results.
const PrivateLazy = ({ children }) => (
  <>
    <NoIndexMeta />
    <Suspense fallback={<PageSpinner />}>{children}</Suspense>
  </>
);

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, loading, hasRole } = useAuth();
  
  if (loading) return <PageSpinner />;
  if (!isAuthenticated) return <Navigate to="/auth/login" replace />;
  if (requiredRole && !hasRole(requiredRole)) return <Navigate to="/" replace />;
  return (
    <>
      <NoIndexMeta />
      {children}
    </>
  );
};

// Lazy wrapper for AccessGuard
const LazyAccessGuard = ({ appSlug, guestLadder, children }) => (
  <Suspense fallback={<PageSpinner />}>
    <AccessGuard appSlug={appSlug} guestLadder={guestLadder}>{children}</AccessGuard>
  </Suspense>
);

function RouteTracker() {
  const location = useLocation();
  const prev = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== prev.current) {
      trackPageView(location.pathname);
      prev.current = location.pathname;
    }
  }, [location]);
  return null;
}

// Delayed ScenarioBot — loads only after main content is interactive
function DeferredBot() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(id);
  }, []);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <ScenarioBot />
    </Suspense>
  );
}

// Переход со старого пути статей на текущий с сохранением слага, хвоста
// запроса и якоря. Регистрируется только когда пути различаются (ру).
function LegacyArticleRedirect() {
  const { slug } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`${ARTICLES_PATH}/${slug}${search}${hash}`} replace />;
}

// Переход на новый адрес с тем же хвостом запроса (utm) и якорем.
function NavigateKeepQuery({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function AppRoutes() {
  // Касса рублей с бэкенда (lib/rubProvider.js): по ней показываются разовые
  // покупки 199 ₽ и «Двойная выгода». Ответ пришёл — всё дерево перерисуется.
  useRubProvider();
  return (
    <>
      <RouteTracker />
      <ScrollManager />
      <RefTracker />
      <YandexMetrikaTracker />
      
      <Routes>
        {/* Eager: Landing — renders instantly, no Suspense */}
        <Route path="/" element={<NewLandingPage />} />

        {/* Категорийные лендинги (4 плашки: Любовь / Деньги / Реализация / Прогноз) */}
        <Route path="/love" element={<CategoryPage slug="love" />} />
        <Route path="/finance" element={<CategoryPage slug="finance" />} />
        <Route path="/success" element={<CategoryPage slug="success" />} />
        <Route path="/forecast" element={<CategoryPage slug="forecast" />} />
        
        {/* Lazy routes — each with own Suspense */}
        <Route path="/horar" element={<><CanonicalRf path="/horar" /><Lazy><HoraryPage /></Lazy></>} />
        {/* Legacy / duplicate landings — noindex to avoid cannibalization */}
        {/* Старая главная с делением программ по тарифам — снята 14.10.2026 (frontend/archive/legacy-landings), сервер отвечает 301 на / */}
        <Route path="/old" element={<Navigate to="/" replace />} />
        
        {/* Auth Routes */}
        <Route path="/platform" element={<PrivateLazy><HomePage /></PrivateLazy>} />
        <Route path="/auth/login" element={<PrivateLazy><LoginPage /></PrivateLazy>} />
        <Route path="/auth/register" element={<PrivateLazy><RegisterPage /></PrivateLazy>} />
        <Route path="/auth/social-callback" element={<Lazy><SocialAuthCallback /></Lazy>} />
        {/* Приземление разовой ссылки из письма (дни 8 и 10 серии) */}
        <Route path="/unlock" element={<Lazy><UnlockPage /></Lazy>} />
        {/* Письмо «Оплата не прошла» — свежий платёж на ту же покупку */}
        <Route path="/pay-again" element={<Lazy><PayAgainPage /></Lazy>} />
        <Route path="/auth/confirm-link" element={<Lazy><ConfirmSocialLinkPage /></Lazy>} />
        <Route path="/login" element={<Navigate to="/auth/login" replace />} />
        <Route path="/register" element={<Navigate to="/auth/register" replace />} />
        <Route path="/auth/verify" element={<PrivateLazy><VerifyPage /></PrivateLazy>} />
        <Route path="/forgot-password" element={<PrivateLazy><ForgotPasswordPage /></PrivateLazy>} />
        <Route path="/reset-password" element={<PrivateLazy><ResetPasswordPage /></PrivateLazy>} />
        <Route path="/apps" element={<Lazy><AppsCatalogPage /></Lazy>} />
        <Route path="/apps/:slug" element={<Lazy><AppDetailPage /></Lazy>} />
        <Route path="/sitemap" element={<Lazy><SitemapPage /></Lazy>} />
        <Route path={`${ARTICLES_PATH}/:slug`} element={<Lazy><CmsPublicPage /></Lazy>} />
        {/* Ру: статьи переехали на прежний адрес старого сайта, а прежний путь
            CMS остаётся живым — переходом внутри приложения. Настоящий 301 для
            прямых заходов и краулеров отдаёт frontend/server.js. */}
        {!ARTICLES_PATH_IS_DEFAULT && (
          <Route path={`${LEGACY_ARTICLES_PATH}/:slug`} element={<LegacyArticleRedirect />} />
        )}
        <Route path="/share/:id" element={<Lazy><SharePreviewPage /></Lazy>} />
        <Route path="/stati" element={<><CanonicalRf path="/stati" /><Lazy><ArticlesListPage /></Lazy></>} />
        <Route path="/articles" element={<Navigate to="/stati" replace />} />
        
        {/* Email Preferences (noindex — private user setting) */}
        <Route path="/email-preferences" element={<PrivateLazy><EmailPreferencesPage /></PrivateLazy>} />
        
        {/* LK Routes */}
        <Route path="/lk/profile" element={<PrivateLazy><ProfilePage /></PrivateLazy>} />
        {/* /lk/daily убран как промежуточная страница, но НЕ удалён: в уже
            ушедших письмах ссылки ведут сюда — редиректим в калькулятор
            транзитов, чтобы старый клик не попал в пустоту. */}
        <Route path="/lk/daily" element={<Navigate to="/transits" replace />} />
        <Route path="/lk/daily/:date" element={<Navigate to="/transits" replace />} />
        <Route path="/dashboard" element={<Navigate to="/lk/profile" replace />} />
        
        {/* Pricing & About */}
        <Route path="/pricing" element={<Lazy><PricingPage /></Lazy>} />
        <Route path="/checkout" element={<Lazy><CheckoutPage /></Lazy>} />
        <Route path="/about" element={<Lazy><AboutPage /></Lazy>} />
        <Route path="/consultation" element={<><CanonicalRf path="/consultation" /><Lazy><ConsultationPage /></Lazy></>} />
        <Route path="/o-nas" element={<Navigate to="/about" replace />} />

        {/* ==================== SEO landing cluster (натальная карта) ====================
            Четыре коммерческих запроса — свой H1, title, description и текст у
            каждого. Калькулятор на всех четырёх тот же, что на главной (хук
            useNatalCalculator + NatalChartWheel + ChartAccordion), см.
            pages/seo/NatalSeoLandingPage.jsx. Раньше эти адреса отдавали 301 на
            `/` (LEGACY_NATAL_CHART_REDIRECTS в server.js) — редирект снят, и
            теперь server.js отдаёт их готовыми: STATIC_SEO_BY_PATH +
            NATAL_LANDING_BODY_SSR. Главная не затронута. */}
        <Route path="/natalnaya-karta" element={<Lazy><NatalSeoLandingPage variant="natalChart" /></Lazy>} />
        <Route path="/natalnaya-karta-besplatno" element={<Lazy><NatalSeoLandingPage variant="natalChartFree" /></Lazy>} />
        <Route path="/raschet-natalnoy-karty" element={<Lazy><NatalSeoLandingPage variant="natalChartCalc" /></Lazy>} />
        <Route path="/rasshifrovka-natalnoy-karty" element={<Lazy><NatalSeoLandingPage variant="natalChartDecode" /></Lazy>} />
        
        {/* FREE Apps */}
        {/* «Где твои деньги» по формулам Астролога. Прежняя страница — в frontend/archive/money-old. */}
        <Route path="/money" element={<Lazy><MoneyV2Page /></Lazy>} />
        {/* Скрытый адрес до переключения — на /money с тем же хвостом (сервер отвечает 301). */}
        <Route path="/money-new" element={<NavigateKeepQuery to="/money" />} />
        {/* «Образ — как тебя видят» по каркасу Астролога. Прежняя страница — в frontend/archive/obraz-old. */}
        <Route path="/obraz" element={<><CanonicalRf path="/obraz" /><Lazy><ObrazV2Page /></Lazy></>} />
        {/* «Нумерология» по методике Колесникова — на основном адресе с 12.10.2026.
            Прежняя страница (pages/apps/num/NumCalculatorPage.jsx) снята с маршрута,
            файл оставлен для отката. Скрытый адрес до запуска — на /numerology
            с тем же хвостом (сервер отвечает 301). */}
        <Route path="/numerology" element={<><CanonicalRf path="/numerology" /><Lazy><NumV2Page /></Lazy></>} />
        <Route path="/numerology-new" element={<NavigateKeepQuery to="/numerology" />} />
        {/* «Профессия» и «Предназначение» по каркасу Астролога — на /work и /uzel с 14.10.2026.
            Прежние страницы — в frontend/archive/work-old и uzel-old. Скрытые адреса до
            запуска — туда же с тем же хвостом (сервер отвечает 301). */}
        <Route path="/work" element={<><CanonicalRf path="/work" /><Lazy><WorkV2Page /></Lazy></>} />
        <Route path="/work-new" element={<NavigateKeepQuery to="/work" />} />
        <Route path="/uzel" element={<><CanonicalRf path="/uzel" /><Lazy><UzelV2Page /></Lazy></>} />
        <Route path="/uzel-new" element={<NavigateKeepQuery to="/uzel" />} />
        
        {/* ASTROFEEL Apps */}
        <Route path="/transits" element={
          <><CanonicalRf path="/transits" /><Lazy><Daily3BirthDataPage /></Lazy></>
        } />
        <Route path="/transits/results" element={
          <LazyAccessGuard appSlug="daily3" guestLadder><Daily3ResultsPage /></LazyAccessGuard>
        } />
        <Route path="/love-periods" element={<><CanonicalRf path="/love-periods" /><Lazy><LovePeriodsPage /></Lazy></>} />
        <Route path="/money-periods" element={<><CanonicalRf path="/money-periods" /><Lazy><MoneyPeriodsPage /></Lazy></>} />
        <Route path="/year" element={<><CanonicalRf path="/year" /><Lazy><YearPage /></Lazy></>} />
        <Route path="/where-to-live" element={<Lazy><WhereToLivePage /></Lazy>} />
        {/* Анкета «ПРО за 5 минут» — пока владелице и админам (pages/anketa) */}
        <Route path="/anketa" element={<Lazy><AnketaPage /></Lazy>} />
        <Route path="/chakry-po-natalnoy-karte" element={<><CanonicalRf path="/chakry-po-natalnoy-karte" /><Lazy><ChakryPage /></Lazy></>} />
        <Route path="/chakry-pary" element={<><CanonicalRf path="/chakry-pary" /><Lazy><ChakryPage tab="para" /></Lazy></>} />
        <Route path="/vlyubit" element={<><CanonicalRf path="/vlyubit" /><Lazy><VlyubitPage /></Lazy></>} />
        {/* Скрытый адрес до переключения; сервер отвечает 301 (server.js LEGACY_APPS_REDIRECTS) */}
        <Route path="/obraz-v2-preview" element={<Navigate to="/obraz" replace />} />
        {/* «Твой месяц» по методике Астролога: ввод и результат на одной странице.
            Прежние «События месяца» — в frontend/archive/events-month-old. */}
        <Route path="/events-month" element={<><CanonicalRf path="/events-month" /><Lazy><MonthV2Page /></Lazy></>} />
        {/* Скрытый адрес до запуска и прежний адрес результата — на /events-month с тем же хвостом (сервер отвечает 301). */}
        <Route path="/events-month/results" element={<NavigateKeepQuery to="/events-month" />} />
        <Route path="/events-month-new" element={<NavigateKeepQuery to="/events-month" />} />
        {/* «Лунный календарь» — публичный раздел без даты рождения; HTML для роботов — server.js (lunarCalendarSsr.js) */}
        <Route path="/lunnyy-kalendar" element={<Lazy><LunarCalendarPage /></Lazy>} />
        <Route path="/lunnyy-kalendar/:kind/:period" element={<Lazy><LunarCalendarPage /></Lazy>} />
        <Route path="/lunnyy-den-segodnya" element={<Lazy><LunarCalendarPage /></Lazy>} />
        
        {/* PRO Apps */}
        {/* «Партнёр» — новая программа с запуска (10.10.2026), вход бесплатный,
            лесенку держит сервер (/api/mum-v2). Прежняя страница — в
            frontend/archive/mum-old. Скрытый адрес до запуска — на /mum с тем
            же хвостом (сервер отвечает 301). */}
        <Route path="/mum" element={<><CanonicalRf path="/mum" /><Lazy><MumV2Page /></Lazy></>} />
        <Route path="/mum-new" element={<NavigateKeepQuery to="/mum" />} />
        <Route path="/para" element={
          <><CanonicalRf path="/para" /><Lazy><ParaCalculatorPage /></Lazy></>
        } />
        
        {/* Legacy redirects */}
        <Route path="/apps/money" element={<Navigate to="/money" replace />} />
        <Route path="/apps/obraz" element={<Navigate to="/obraz" replace />} />
        <Route path="/apps/num" element={<Navigate to="/numerology" replace />} />
        <Route path="/apps/daily3" element={<Navigate to="/transits" replace />} />
        <Route path="/apps/daily3/results" element={<Navigate to="/transits/results" replace />} />
        <Route path="/apps/uzel" element={<Navigate to="/uzel" replace />} />
        <Route path="/apps/work" element={<Navigate to="/work" replace />} />
        <Route path="/apps/mum" element={<Navigate to="/mum" replace />} />
        <Route path="/apps/para" element={<Navigate to="/para" replace />} />
        <Route path="/apps/women" element={<NavigateKeepQuery to="/women" />} />
        
        {/* Billing */}
        <Route path="/billing" element={<ProtectedRoute><Lazy><BillingPage /></Lazy></ProtectedRoute>} />
        
        {/* Landing Pages — landing variants are noindex (duplicate intent with /numerology, /obraz) */}
        {/* Старые посадочные с метками тарифов — сняты 14.10.2026 (frontend/archive/legacy-landings), сервер отвечает 301 на программы */}
        <Route path="/landing/num" element={<Navigate to="/numerology" replace />} />
        <Route path="/landing/obraz" element={<Navigate to="/obraz" replace />} />
        {/* «Женские события» — новая программа с запуска (03.10.2026). Прежняя
            страница — в frontend/archive/women-old. Скрытый адрес до запуска —
            на /women с тем же хвостом (сервер отвечает 301). */}
        <Route path="/women" element={<><CanonicalRf path="/women" /><Lazy><WomenV2Page /></Lazy></>} />
        <Route path="/women-new" element={<NavigateKeepQuery to="/women" />} />
        <Route path="/vote" element={<PrivateLazy><VotePage /></PrivateLazy>} />
        <Route path="/l/:slug" element={<Lazy><LandingTemplate /></Lazy>} />
        
        {/* Protected User Routes */}
        <Route path="/account" element={<ProtectedRoute><Lazy><AccountPage /></Lazy></ProtectedRoute>} />
        <Route path="/account/payments" element={<ProtectedRoute><Lazy><PaymentsHistoryPage /></Lazy></ProtectedRoute>} />
        {/* Сохранённый расчёт — открыт владельцу навсегда, тариф не проверяется */}
        <Route path="/account/result/:resultId" element={<ProtectedRoute><Lazy><SavedResultPage /></Lazy></ProtectedRoute>} />
        <Route path="/subscribe" element={<PrivateLazy><SubscribePage /></PrivateLazy>} />
        
        {/* Payment Routes */}
        <Route path="/payment/success" element={<PrivateLazy><PaymentSuccessPage /></PrivateLazy>} />
        <Route path="/payment/cancel" element={<PrivateLazy><PaymentCancelPage /></PrivateLazy>} />
        <Route path="/support/payment-help" element={<Lazy><PaymentHelpPage /></Lazy>} />
        <Route path="/payment-manual" element={<PrivateLazy><ManualPaymentPage /></PrivateLazy>} />
        
        {/* Manager Routes */}
        <Route path="/manager" element={<ProtectedRoute requiredRole="manager"><Lazy><ManagerPage /></Lazy></ProtectedRoute>} />
        <Route path="/manager/messages" element={<ProtectedRoute requiredRole="manager"><Lazy><ManagerPage /></Lazy></ProtectedRoute>} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><Lazy><AdminPage /></Lazy></ProtectedRoute>} />
        <Route path="/admin/*" element={<ProtectedRoute requiredRole="admin"><Lazy><AdminPage /></Lazy></ProtectedRoute>} />
        
        {/* Legal Pages */}
        <Route path="/privacy" element={<Lazy><PrivacyPage /></Lazy>} />
        <Route path="/offer" element={<Lazy><OfferPage /></Lazy>} />
        <Route path="/partner-offer" element={<Lazy><PartnerOfferPage /></Lazy>} />
        {/* Согласие на рассылку — документ галочки шага 1 регистрации и кабинета */}
        <Route path="/mailing-consent" element={<Lazy><MailingConsentPage /></Lazy>} />
        <Route path="/contacts" element={<><CanonicalRf path="/contacts" /><Lazy><ContactsLegalPage /></Lazy></>} />
        <Route path="/terms" element={<Lazy><TermsPage /></Lazy>} />
        
        {/* Fallback */}
        <Route path="*" element={<Lazy><NotFoundPage /></Lazy>} />
      </Routes>
    </>
  );
}

import InstallPWAButton from '@/components/InstallPWAButton';
import ImpersonationBar from '@/components/layout/ImpersonationBar';
import ArticleReaderHost from '@/components/natal/ArticleReaderHost';

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ImpersonationBar />
          <AppRoutes />
          <Toaster position="top-right" />
          <DeferredBot />
          <InstallPWAButton />
          {/* Читалка статей поверх карты — заголовки расшифровки натала. */}
          <ArticleReaderHost />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
